import { useEffect, useState, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MonitorSpeaker, Plus, Trash2, Download, Check, X, Loader2, Cpu } from "lucide-react";

const DAW_NOTE = {
  reaper: "REAPER (full companion): the agent runs a ReaScript that builds the named tracks and drops your MIDI in place, ready for your own instruments and beat packs.",
  ableton: "Ableton Live (companion): the agent places the MIDI clips plus an arrangement guide into a folder you import into your Live set, then you load your packs.",
  flstudio: "FL Studio (package): FL has no external automation API, so the agent drops the MIDI plus a channel and mixer map into a watch folder. You import it and assign your own kits and instruments.",
};

export const DevicePairing = () => {
  const [devices, setDevices] = useState([]);
  const [daws, setDaws] = useState([]);
  const [open, setOpen] = useState(false);
  const [daw, setDaw] = useState("reaper");
  const [session, setSession] = useState(null);
  const [agentCode, setAgentCode] = useState("");
  const [approving, setApproving] = useState(false);
  const pollRef = useRef(null);

  const loadDevices = () => api.get("/devices").then((r) => setDevices(r.data));
  useEffect(() => {
    loadDevices();
    api.get("/pairable-daws").then((r) => setDaws(r.data));
    const qc = new URLSearchParams(window.location.search).get("agent_code");
    if (qc) { setAgentCode(qc); setOpen(true); }
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const approveByCode = async () => {
    const code = agentCode.trim().toUpperCase();
    if (!code) return;
    setApproving(true);
    try {
      const r = await api.post("/pairing/approve", { user_code: code, daw });
      toast.success("Agent paired");
      setAgentCode(""); await loadDevices(); setOpen(false);
    } catch (e) { toast.error(e?.response?.status === 404 ? "Code not found — check your agent" : "Could not pair"); }
    finally { setApproving(false); }
  };

  const startPair = async () => {
    const r = await api.post("/pairing-sessions", { daw });
    setSession(r.data);
    pollRef.current = setInterval(async () => {
      const s = await api.get(`/pairing-sessions/${r.data.pairing_id}`);
      setSession((prev) => ({ ...prev, ...s.data }));
      if (["approved", "denied"].includes(s.data.status)) clearInterval(pollRef.current);
    }, 2000);
  };

  const simulate = async () => {
    if (!session) return;
    await api.post(`/pairing-sessions/${session.pairing_id}/claim`, {
      code: session.code, device_name: `${daw === "reaper" ? "Studio" : "My"} ${navigator.platform || "PC"}`,
      platform: navigator.platform || "desktop", daw,
    });
    toast.info("Agent claimed, approve to connect");
  };

  const approve = async () => {
    const r = await api.post(`/pairing-sessions/${session.pairing_id}/approve`);
    toast.success("Device connected");
    clearInterval(pollRef.current);
    await loadDevices();
    setSession((prev) => ({ ...prev, status: "approved", device_id: r.data.device_id }));
  };

  const revoke = async (id) => { await api.delete(`/devices/${id}`); loadDevices(); };
  const closeAll = () => { clearInterval(pollRef.current); setSession(null); setOpen(false); };

  return (
    <div className="card-surface rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><MonitorSpeaker className="w-5 h-5 text-violet" /><h2 className="font-display font-semibold text-lg text-slate-50">Paired devices</h2></div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) closeAll(); }}>
          <DialogTrigger asChild>
            <Button data-testid="pair-device-btn" className="rounded-full bg-violet-500 hover:bg-violet-400 font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Pair a device</Button>
          </DialogTrigger>
          <DialogContent className="card-surface border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Pair the Aura Agent</DialogTitle>
              <DialogDescription className="text-slate-400">The Aura Agent is a small app that runs on your computer and bridges AuraSession to your DAW. It does not run inside the DAW.</DialogDescription>
            </DialogHeader>
            {!session ? (
              <div className="space-y-4 py-2">
                <div>
                  <label className="label-mono text-slate-400">Target DAW</label>
                  <Select value={daw} onValueChange={setDaw}>
                    <SelectTrigger data-testid="pair-daw-select" className="mt-1.5 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="card-surface border-white/10">
                      {daws.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`pair-daw-${d.id}`}>{d.name} · {d.mode}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-lg bg-black/30 border border-white/10 p-3 text-xs text-slate-400 leading-relaxed" data-testid="pair-daw-note">
                  <span className="text-slate-200">How it works: </span>{DAW_NOTE[daw] || "The Aura Agent bridges AuraSession to this DAW: it drops the MIDI plus a channel and mixer map into a folder you import, then you assign your own kits and instruments."}
                </div>

                <div className="rounded-xl border border-white/10 p-3 space-y-2">
                  <p className="text-slate-200 text-sm font-medium">Already running the Aura Agent?</p>
                  <p className="text-slate-500 text-xs">Pairs with the secure device-code standard (RFC 8628). Enter the code your agent shows.</p>
                  <div className="flex items-center gap-2">
                    <input data-testid="agent-code-input" value={agentCode} onChange={(e) => setAgentCode(e.target.value.toUpperCase())}
                      placeholder="WXYZ-1234" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 h-10 text-sm text-slate-100 tracking-widest outline-none placeholder:text-slate-600" />
                    <Button data-testid="agent-code-approve-btn" onClick={approveByCode} disabled={approving || !agentCode.trim()} className="h-10 rounded-lg bg-cyan-400 text-black hover:bg-cyan-300 font-semibold disabled:opacity-50">{approving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pair"}</Button>
                  </div>
                </div>

                <a data-testid="download-generic-agent" href={`${API}/pairing/agent?daw=${daw}`} target="_blank" rel="noreferrer" className="block text-center text-xs text-slate-400 hover:text-cyan underline">Don't have it? Download the Aura Agent (Python, no dependencies)</a>

                <div className="flex items-center gap-3 text-[11px] text-slate-600"><div className="flex-1 h-px bg-white/10" /> or generate a code here <div className="flex-1 h-px bg-white/10" /></div>
                <Button data-testid="pair-generate-btn" onClick={startPair} className="w-full rounded-full bg-violet-500 hover:bg-violet-400 font-semibold h-11">Generate pairing code</Button>
              </div>
            ) : session.status === "approved" ? (
              <div className="py-4 text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-cyan-400/15 flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-cyan" /></div>
                <p className="font-display text-lg text-slate-50">Device connected</p>
                <a data-testid="download-agent-link" href={`${API}/devices/${session.device_id}/agent-script`} target="_blank" rel="noreferrer">
                  <Button className="rounded-full bg-cyan-400 text-black hover:bg-cyan-300 font-semibold"><Download className="w-4 h-4 mr-1.5" /> Download Aura Agent</Button>
                </a>
                <p className="text-slate-500 text-xs">Run <span className="font-mono-a">python aura_agent.py</span> on that machine to receive pushed builds.</p>
              </div>
            ) : (
              <div className="py-2 space-y-4">
                <div className="flex justify-center bg-white rounded-xl p-4">
                  <QRCodeSVG value={session.qr_payload} size={168} />
                </div>
                <div className="text-center">
                  <p className="label-mono text-slate-500">Pairing code</p>
                  <p className="font-display font-extrabold text-3xl tracking-[0.3em] text-cyan mt-1">{session.code}</p>
                </div>
                {session.status === "claimed" ? (
                  <div className="bg-black/30 rounded-xl p-4 text-center space-y-3">
                    <p className="text-slate-200 text-sm">{session.device_info?.device_name} wants to connect ({session.device_info?.daw?.toUpperCase()})</p>
                    <div className="flex gap-2 justify-center">
                      <Button data-testid="pair-approve-btn" onClick={approve} className="rounded-full bg-cyan-400 text-black hover:bg-cyan-300 font-semibold"><Check className="w-4 h-4 mr-1.5" /> Approve</Button>
                      <Button data-testid="pair-deny-btn" onClick={closeAll} variant="outline" className="rounded-full border-white/15"><X className="w-4 h-4 mr-1.5" /> Deny</Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-2">
                    <p className="text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Waiting for the agent…</p>
                    <button data-testid="pair-simulate-btn" onClick={simulate} className="text-xs text-slate-500 hover:text-slate-300 underline">Don't have the agent yet? Simulate a connection</button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      {devices.length === 0 ? (
        <p className="text-slate-600 text-sm">No devices paired. Pair the Aura Agent to push builds into REAPER, Ableton or FL Studio.</p>
      ) : (
        <div className="space-y-2" data-testid="devices-list">
          {devices.map((d) => (
            <div key={d.id} className="flex items-center justify-between bg-black/30 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <Cpu className={`w-4 h-4 ${d.status === "connected" ? "text-cyan" : "text-slate-600"}`} />
                <div>
                  <p className="text-slate-200 text-sm">{d.device_name} · <span className="text-cyan uppercase text-xs">{d.daw}</span></p>
                  <p className="text-slate-600 text-xs">{d.platform} · {d.status}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {d.status === "connected" && (
                  <a href={`${API}/devices/${d.id}/agent-script`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-cyan" title="Download agent"><Download className="w-4 h-4" /></a>
                )}
                <button data-testid={`revoke-device-${d.id}`} onClick={() => revoke(d.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
